import {Box, Button, GlobalStyles, Stack, Typography} from "@mui/material";
import {c, mono, maxW, pillButtonSx} from "../landingTokens";
import {VoiceBars} from "./VoiceBars";

const scrollTo = (id) => () =>
  document.getElementById(id)?.scrollIntoView({behavior: "smooth", block: "start"});

export const Hero = () => (
  <Box component="section" data-screen-label="Hero" sx={{position: "relative", px: 2.75, pt: 12, textAlign: "center", overflow: "hidden"}}>
    <GlobalStyles
      styles={{
        "@keyframes bfPulse": {"0%,100%": {transform: "scaleY(0.22)"}, "50%": {transform: "scaleY(1)"}},
        "@keyframes bfDrift": {
          "0%,100%": {transform: "translate3d(0,0,0) scale(1)"},
          "50%": {transform: "translate3d(3%,-4%,0) scale(1.08)"},
        },
      }}
    />

    {/* Purple bloom behind the headline */}
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: -320,
        left: "50%",
        transform: "translateX(-50%)",
        width: 1300,
        height: 860,
        background:
          "radial-gradient(ellipse at 50% 40%, rgba(132,145,247,0.55), rgba(215,232,244,0.55) 45%, rgba(254,254,254,0) 70%)",
        filter: "blur(10px)",
        pointerEvents: "none",
        animation: "bfDrift 14s ease-in-out infinite",
      }}
    />

    <Box sx={{position: "relative", maxWidth: 1000, mx: "auto"}}>
      <Typography sx={{fontFamily: mono, fontSize: "11.5px", letterSpacing: "0.16em", textTransform: "uppercase", color: c.purpleDeep, mb: 2.75}}>
        OpenBrain · AI Voice Agent
      </Typography>

      <Typography
        component="h1"
        sx={{fontSize: "clamp(46px, 8.2vw, 112px)", lineHeight: 0.95, letterSpacing: "-0.045em", fontWeight: 600, textWrap: "balance"}}
      >
        Cut contact center<br />workload by{" "}<br/>
        <Box
          component="span"
          sx={{
            background: `linear-gradient(100deg, ${c.purple}, ${c.purpleDeep})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          50–70%
        </Box>
        .
      </Typography>

      <Typography sx={{maxWidth: 620, mx: "auto", mt: 3.75, fontSize: 21, lineHeight: 1.45, color: c.inkSoft, textWrap: "pretty"}}>
        Our AI agent answers inbound calls, understands intent and resolves queries instantly — then hands off to your team with full context.
      </Typography>

      <Stack direction="row" gap={1.5} justifyContent="center" flexWrap="wrap" sx={{mt: 4.25}}>
        <Button
          onClick={scrollTo("contact")}
          variant="contained"
          sx={{...pillButtonSx, bgcolor: c.ink, color: c.white, boxShadow: "none", "&:hover": {bgcolor: c.purpleDeep, boxShadow: "none"}}}
        >
          Get a free demo
        </Button>
        <Button
          onClick={scrollTo("how")}
          sx={{
            ...pillButtonSx,
            color: c.ink,
            bgcolor: "rgba(255,255,255,0.7)",
            border: "1px solid rgba(44,43,42,0.14)",
            "&:hover": {borderColor: "rgba(44,43,42,0.35)", bgcolor: c.white},
          }}
        >
          Hear it in action ›
        </Button>
      </Stack>

      <Typography sx={{fontFamily: mono, fontSize: 11, color: c.inkMuted, mt: 2.5, letterSpacing: "0.05em"}}>
        Runs on your Avaya · EN · MK · SR · HR · GDPR-compliant
      </Typography>
    </Box>

    {/* Console window, cropped by the section's bottom edge */}
    <Box sx={{position: "relative", maxWidth: maxW, mx: "auto", mt: 7.75}}>
      <Box
        sx={{
          borderRadius: "28px 28px 0 0",
          overflow: "hidden",
          border: `1px solid rgba(44,43,42,0.1)`,
          borderBottom: "none",
          bgcolor: c.white,
          boxShadow: "0 40px 80px -40px rgba(92,107,232,0.35)",
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.875} sx={{height: 38, px: 2, bgcolor: c.bgSoft, borderBottom: `1px solid rgba(44,43,42,0.07)`}}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{width: 9, height: 9, borderRadius: "50%", bgcolor: "#e3e3e8"}} />
          ))}
          <Typography sx={{fontFamily: mono, fontSize: "10.5px", color: c.inkMuted, ml: 1.5}}>
            openbrain · live call console
          </Typography>
        </Stack>

        <Box
          sx={{
            aspectRatio: "16 / 8.6",
            display: "grid",
            placeItems: "center",
            background: `linear-gradient(180deg, ${c.bgTint}, ${c.white})`,
            backgroundImage: "repeating-linear-gradient(135deg, rgba(132,145,247,0.09) 0 2px, transparent 2px 12px)",
          }}
        >
          <Box sx={{textAlign: "center", p: 3}}>
            <VoiceBars />
            <Typography sx={{fontFamily: mono, fontSize: 12, color: c.inkMuted, mt: 2.5, letterSpacing: "0.16em", textTransform: "uppercase"}}>
              Live call console · coming soon
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);
